import {
  Button,
  ButtonGroup,
  DataTable,
  DataTableWC,
  Icon,
  Image,
  PageBody,
  PageSection,
  Search,
  Spacer,
  Stack,
  Text,
  TextTitle,
  TextCaption,
} from '@pypestream/design-system';
import {
  dateFnsLocales,
  fallbackLanguage,
  TranslationComponent,
  useTranslation,
} from '@pypestream/translations';
import { formatTimeWithTimezone } from '@pypestream/utils';
import { html } from 'lit/static-html.js';
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_SQUARE_ICON_BASE64, Loader } from '../../../components';
import {
  OrganizationRoleNames,
  useAuthRole,
  useFormatTimeWithTimezone,
  useUserTimeZone,
} from '../../../hooks';
import {
  sendManagerEvent,
  useManagerCtxSelector,
  useManagerStateMatches,
  useManagerStateMatchesAllOf,
} from '../../../xstate/app.xstate';
import { IPage } from '../../types';

export const Organizations: React.FC<IPage> = ({ title }) => {
  const loaded = useManagerStateMatches('orgRelated.ready.childOrgs.loaded');
  const isReadyToLoad = useManagerStateMatchesAllOf([
    'orgRelated.ready.childOrgs.idle',
    'orgRelated.ready.currentOrg.selected',
  ]);

  const { childOrgs, defaultLanguage, organizationId } = useManagerCtxSelector(
    (ctx) => ({
      childOrgs: ctx.childOrgs,
      defaultLanguage: ctx.userInfo?.defaultLanguage,
      organizationId: ctx.selectedOrgId,
    })
  );

  const isAdminRole = useAuthRole([
    OrganizationRoleNames.ADMIN,
    OrganizationRoleNames.SUPER_ADMIN,
  ]);

  const navigate = useNavigate();

  const tableRef = useRef<DataTableWC>(null);
  const [t] = useTranslation();
  const userTimeZone = useUserTimeZone();

  const currentOrgCreationDate = useFormatTimeWithTimezone({
    fromDate: childOrgs.currentOrg?.createdAt,
  });

  useEffect(() => {
    if (isReadyToLoad) {
      sendManagerEvent('manager.childOrgs.loadChildOrgs');
    }
  }, [isReadyToLoad]);

  return (
    <>
      <PageBody background="none" width="large">
        <>
          {!loaded ? (
            <PageSection>
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Loader relative />
              </div>
            </PageSection>
          ) : (
            <>
              <Stack alignItems="center" justifyContent="space-between">
                <Stack
                  gutter="small"
                  justifyContent="start"
                  alignItems="center"
                  direction="row"
                  nowrap
                >
                  <Image
                    width="80px"
                    height="80px"
                    alt={childOrgs.currentOrg?.name || 'Organization Logo'}
                    src={childOrgs.currentOrg?.pictureFile?.url}
                    fallback={DEFAULT_SQUARE_ICON_BASE64}
                  />
                  <Stack gutter="none" display="inline-flex" direction="column">
                    <TextTitle test-id="org-name">
                      {childOrgs.currentOrg?.name}
                    </TextTitle>
                    <TextCaption>
                      {t('manager/organizations:childOrgsList.created', {
                        date: currentOrgCreationDate,
                      })}
                    </TextCaption>
                  </Stack>
                </Stack>
                {childOrgs.currentOrg && isAdminRole && (
                  <ButtonGroup>
                    <Button
                      test-id="edit-org-button"
                      size="large"
                      variant="secondary"
                      onClick={() =>
                        navigate(
                          `/organization/${childOrgs.currentOrg?.id}/edit`
                        )
                      }
                    >
                      <Icon slot="prefix" name="edit" size="xsmall" />
                      <TranslationComponent i18nKey="manager/organizations:childOrgsList.editOrg">
                        Edit Org
                      </TranslationComponent>
                    </Button>
                  </ButtonGroup>
                )}
              </Stack>
              <Spacer size="large" />
              <PageSection size="small">
                <Stack alignItems="center" justifyContent="end">
                  <TextTitle size="xsmall" style={{ marginInlineEnd: 'auto' }}>
                    {t('manager/organizations:childOrgsList.childOrgs', {
                      count: childOrgs.count,
                      defaultValue: 'Child Organizations ({{count}})',
                    })}
                  </TextTitle>
                  <Search
                    size="large"
                    style={{ maxWidth: '240px' }}
                    onInput={(e) => {
                      tableRef.current?.table.setGlobalFilter(
                        String(e.target?.value) || ''
                      );
                    }}
                  ></Search>
                  {isAdminRole && (
                    <ButtonGroup>
                      <Button
                        test-id="create-child-org-btn"
                        size="large"
                        onClick={() =>
                          navigate(
                            `/organization/${organizationId}/child-orgs/create`
                          )
                        }
                      >
                        <Icon slot="prefix" name="add" size="xsmall" />
                        <TranslationComponent i18nKey="manager/organizations:childOrgsList.createOrg">
                          Create Child Org
                        </TranslationComponent>
                      </Button>
                    </ButtonGroup>
                  )}
                </Stack>
                <Spacer size="large"></Spacer>
                <DataTable
                  test-id="child-orgs-table"
                  size="small"
                  ref={tableRef}
                  filterColumns={['name', 'teams', 'projects', 'organizations']}
                  columns={[
                    {
                      accessorKey: 'name',
                      header: () =>
                        t('manager/organizations:childOrgsList.table.name', {
                          defaultValue: 'Name',
                        }) || '',
                      cell: ({ row, renderValue }) => html`
                        <ps-stack
                          gutter="small"
                          justifyContent="start"
                          alignItems="center"
                          direction="row"
                          nowrap
                        >
                          <ps-avatar
                            style="flex-shrink: 0"
                            size="medium"
                            src="${row.original.pictureFile?.url || ''}"
                            fallback=${DEFAULT_SQUARE_ICON_BASE64}
                            alt=${childOrgs.currentOrg?.name ||
                            'Organization Logo'}
                          ></ps-avatar>

                          <ps-text-subtitle
                            size="small"
                            truncate
                            style="min-width: 0px"
                            >${row.original.name}</ps-text-subtitle
                          >
                        </ps-stack>
                      `,
                    },
                    {
                      accessorKey: 'users',
                      header: () =>
                        t('manager/organizations:childOrgsList.table.users', {
                          defaultValue: 'Users',
                        }) || '',
                      cell: ({ row, renderValue }) =>
                        html`<ps-text-body variant="secondary"
                          >${row.original.users?.count || '0'}</ps-text-body
                        >`,
                    },
                    {
                      accessorKey: 'teams',
                      accessorFn: (row) =>
                        row.teams?.rows?.map((team) => team.name).join(', '),
                      header: () =>
                        t('manager/organizations:childOrgsList.table.teams', {
                          defaultValue: 'Teams',
                        }) || '',
                      cell: ({ row, renderValue }) =>
                        html`<ps-text-body variant="secondary"
                          >${row.original.teams?.rows
                            ?.map?.((team) => team.name)
                            .join(', ')}</ps-text-body
                        >`,
                    },
                    {
                      accessorKey: 'projects',
                      accessorFn: (row) =>
                        row.projects?.rows
                          ?.map((project) => project.name)
                          .join(', '),
                      header: () =>
                        t(
                          'manager/organizations:childOrgsList.table.projects',
                          {
                            defaultValue: 'Projects',
                          }
                        ) || '',
                      cell: ({ row, renderValue }) =>
                        html`<ps-text-body variant="secondary"
                          >${row.original.projects?.rows
                            ?.map?.((project) => project.name)
                            .join(', ')}</ps-text-body
                        >`,
                    },
                    {
                      accessorKey: 'organizations',
                      accessorFn: (row) =>
                        row.allChildAccounts?.map((org) => org.name).join(', '),
                      header: () =>
                        t(
                          'manager/organizations:childOrgsList.table.childOrgs',
                          {
                            defaultValue: 'Child Orgs',
                          }
                        ) || '',
                      cell: ({ row, renderValue }) =>
                        html`<ps-text-body variant="secondary"
                          >${row.original.allChildAccounts
                            ?.map?.((org) => org.name)
                            .join(', ')}</ps-text-body
                        >`,
                    },
                    {
                      accessorKey: 'updated',
                      accessorFn: (row) => row.updatedAt?.toString(),
                      header: () =>
                        t('manager/organizations:childOrgsList.table.updated', {
                          defaultValue: 'Last updated',
                        }) || '',
                      cell: ({ row, renderValue }) =>
                        html`<div style="min-width: 90px">
                          <ps-text-body
                            variant="secondary"
                            textAlign="end"
                            nowrap
                          >
                            ${formatTimeWithTimezone({
                              fromDate: row.original.updatedAt,
                              locale:
                                dateFnsLocales[
                                  defaultLanguage || fallbackLanguage
                                ],
                              userTimeZone,
                            })}</ps-text-body
                          >
                        </div>`,
                      meta: {
                        align: 'end',
                      },
                    },
                  ]}
                  data={childOrgs.rows}
                  onRowSelectionChange={(event) => {
                    if (event.detail.selected && event.detail.selected.length) {
                      event.detail.selected.map((item) => {
                        const dataTable = event.currentTarget as DataTableWC;

                        dataTable['table'].resetRowSelection();

                        sendManagerEvent({
                          type: 'manager.selectChildOrg',
                          id: item.original.id,
                        });

                        if (isAdminRole) {
                          navigate(`/organization/${item.original.id}/edit`);
                        } else {
                          navigate(
                            `/organization/${item.original.id}/child-orgs`
                          );
                        }

                        return item;
                      });
                    }
                  }}
                  enableRowSelection
                ></DataTable>

                {!childOrgs ||
                  (!childOrgs.rows?.length && (
                    <Text
                      size="xsmall"
                      textAlign="center"
                      className="u-margin-top-xlarge"
                    >
                      <TranslationComponent i18nKey="manager/organizations:childOrgsList.createToGetStarted">
                        Create an org to get started.
                      </TranslationComponent>
                    </Text>
                  ))}
              </PageSection>
            </>
          )}
        </>
      </PageBody>
    </>
  );
};
